/**
 * billingLedgerService — the read side of CYV-native billing (autopay slice 2).
 *
 * Invoices and ledger entries are written ONLY by Cloud Functions (the rules
 * deny every client write). This module reads them, and owns the one piece of
 * billing logic that deliberately lives on the client rather than in the
 * database: working out which invoices a family's payments have covered.
 *
 * WHY ALLOCATION IS DERIVED, NOT STORED
 * ---------------------------------------------------------------------------
 * There is no per-invoice `amountPaid` field and no allocation engine. A
 * payment is money against the ACCOUNT; an invoice is an immutable statement of
 * one month's charges. Per-invoice "paid / part-paid / outstanding" is computed
 * here by applying payments to invoices oldest-first, as a pure function over
 * data that is already loaded.
 *
 * The trade is deliberate. A stored allocation is a second source of truth that
 * can disagree with the ledger; a derived one cannot. If this function is ever
 * wrong the display is wrong and a refresh fixes it — nothing is corrupted.
 * For a studio billing a flat monthly amount to ~75 families that is plainly
 * the right side of the trade; a real allocation engine earns its keep only
 * with partial payments and per-invoice disputes, and CYV has neither.
 *
 * SIGN CONVENTION (identical to functions/billingLedger.js — read that header):
 *   POSITIVE amountCents = the family owes MORE  (invoice issued, refund)
 *   NEGATIVE amountCents = the family owes LESS  (payment, credit, write-off)
 * So a POSITIVE balance is outstanding and a NEGATIVE one is paid ahead.
 */

import { db, functions } from '../firebase';
import {
	collection,
	doc,
	onSnapshot,
	query,
	where,
	orderBy,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export const INVOICES = 'invoices';
export const BILLING_LEDGER = 'billingLedger';
export const ENROLLMENTS = 'enrollments';
export const STUDENTS = 'students';

/** Entry types that reduce what a family owes — i.e. money in, or forgiven. */
const CREDITING_TYPES = new Set(['payment', 'credit', 'writeoff']);

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * Live-subscribe to a family's invoices, newest billing month first.
 *
 * RULES ARE NOT FILTERS. The `where('familyId','==',familyId)` below is not a
 * convenience — it must match the security rule's predicate exactly or the
 * whole listen is denied rather than narrowed. The composite index for this
 * ordering ships in firestore.indexes.json; the emulator answers without one,
 * so its absence is invisible to tests and would surface as a prod-only 400.
 */
export function subscribeToInvoices(familyId, onData, onError) {
	if (!familyId) {
		onData([]);
		return () => {};
	}
	const q = query(
		collection(db, INVOICES),
		where('familyId', '==', familyId),
		orderBy('periodMonth', 'desc')
	);
	return onSnapshot(
		q,
		(snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
		(err) => {
			console.error('[billing] invoice subscription failed:', err);
			if (onError) onError(err);
		}
	);
}

/**
 * Live-subscribe to every invoice in ONE billing month, across all families.
 *
 * Deliberately NOT filtered to status:'open' in the query. The admin panel has
 * to tell three states apart — no invoices generated yet, invoices generated
 * and open, invoices generated then voided — and a query that returns only
 * open rows collapses the first and third into the same empty result. That
 * distinction is not academic: on 2026-08-31 the two QA invoices sat voided
 * while September had not been generated at all, and "nothing here" was the
 * wrong reading of both.
 *
 * A single equality filter needs no composite index.
 */
export function subscribeToInvoicesForPeriod(periodMonth, onData, onError) {
	if (!periodMonth) {
		onData([]);
		return () => {};
	}
	const q = query(collection(db, INVOICES), where('periodMonth', '==', periodMonth));
	return onSnapshot(
		q,
		(snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
		(err) => {
			console.error('[billing] period invoice subscription failed:', err);
			if (onError) onError(err);
		}
	);
}

/**
 * Live-subscribe to a family's tuition enrollments.
 *
 * THIS IS THE COLLECTION THAT DECIDES WHO GETS INVOICED, and it is the whole
 * reason this subscription exists. `performGenerateInvoices` queries
 * enrollments on (type, seasonLabel, status) and reads NOTHING else — not
 * `students.status`, not `timeSlots`. So a student can be Active on the roster
 * and holding an assigned weekly slot and still be correctly un-billed, which
 * is the separation core.md → Tuition describes and which had no UI until now.
 *
 * DELIBERATELY NO `orderBy`. A single equality filter is served by the
 * automatic single-field index, so this ships without touching
 * firestore.indexes.json — i.e. it stays a hosting-only change. Ordering is
 * done in the component over a handful of docs. Adding an orderBy here means
 * a composite index and a rules/indexes deploy for no user-visible gain.
 *
 * Reads are allowed by `isAdmin() || isOwner()` (firestore.rules) and the only
 * caller is the admin-gated Billing page.
 */
export function subscribeToEnrollments(familyId, onData, onError) {
	if (!familyId) {
		onData([]);
		return () => {};
	}
	const q = query(collection(db, ENROLLMENTS), where('familyId', '==', familyId));
	return onSnapshot(
		q,
		(snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
		(err) => {
			console.error('[billing] enrollment subscription failed:', err);
			if (onError) onError(err);
		}
	);
}

/**
 * Live-subscribe to a family's students.
 *
 * Needed so the Billing page can offer to START billing a student who has no
 * enrollment yet — the case the enrollment list alone cannot show, because a
 * student with no enrollment doc has nothing to render. That gap is not
 * hypothetical: on 2026-08-21 two students (both converted from trial leads)
 * held assigned weekly slots with no tuition enrollment, so they were
 * scheduled to be taught and absent from every invoice run.
 *
 * Same shape as subscribeToEnrollments — single equality filter, no orderBy,
 * so it rides the automatic single-field index and needs no indexes deploy.
 * Reads are allowed for admin/owner (firestore.rules → students).
 */
export function subscribeToFamilyStudents(familyId, onData, onError) {
	if (!familyId) {
		onData([]);
		return () => {};
	}
	const q = query(collection(db, STUDENTS), where('familyId', '==', familyId));
	return onSnapshot(
		q,
		(snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
		(err) => {
			console.error('[billing] student subscription failed:', err);
			if (onError) onError(err);
		}
	);
}

/** Live-subscribe to a family's ledger entries, newest first. */
export function subscribeToLedger(familyId, onData, onError) {
	if (!familyId) {
		onData([]);
		return () => {};
	}
	const q = query(
		collection(db, BILLING_LEDGER),
		where('familyId', '==', familyId),
		orderBy('createdAt', 'desc')
	);
	return onSnapshot(
		q,
		(snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
		(err) => {
			console.error('[billing] ledger subscription failed:', err);
			if (onError) onError(err);
		}
	);
}

/** Live-subscribe to one invoice (admin detail view). */
export function subscribeToInvoice(invoiceId, onData, onError) {
	if (!invoiceId) {
		onData(null);
		return () => {};
	}
	return onSnapshot(
		doc(db, INVOICES, invoiceId),
		(snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
		(err) => {
			console.error('[billing] invoice subscription failed:', err);
			if (onError) onError(err);
		}
	);
}

// ---------------------------------------------------------------------------
// Admin callables (staff-only server-side; a family calling one is denied)
// ---------------------------------------------------------------------------

const call = (name) => async (payload) => {
	const res = await httpsCallable(functions, name)(payload || {});
	return res.data;
};

export const enrollStudentForSeason = call('enrollStudentForSeason');
export const withdrawTuitionEnrollment = call('withdrawTuitionEnrollment');
export const generateInvoices = call('generateInvoices');
// The one callable that MOVES MONEY (autopay slice 3). dryRun (the server
// default) returns the per-family plan and touches nothing; { dryRun: false }
// executes the batch. See functions/billingCharge.js for the full design.
export const chargeTuitionInvoices = call('chargeTuitionInvoices');
export const voidInvoice = call('voidInvoice');
export const recordBillingPayment = call('recordBillingPayment');
export const adjustBillingLedger = call('adjustBillingLedger');
export const reconcileBillingBalance = call('reconcileBillingBalance');

// ---------------------------------------------------------------------------
// Pure helpers — no Firebase, unit-testable in isolation
// ---------------------------------------------------------------------------

/**
 * Format integer cents as US currency. Negative amounts are rendered as a
 * parenthesised credit rather than a minus sign, because "-$240.00" next to a
 * balance reads ambiguously to a parent — they cannot tell whether it means
 * they owe it or are owed it.
 */
export function formatCents(cents, { showCredit = true } = {}) {
	if (typeof cents !== 'number' || !Number.isFinite(cents)) return '—';
	const abs = Math.abs(cents);
	const s = `$${(abs / 100).toFixed(2)}`;
	if (cents < 0 && showCredit) return `${s} credit`;
	return s;
}

/**
 * A one-line, parent-readable statement of where an account stands.
 * Deliberately plain: this sentence is the whole answer for most families most
 * months, and hedged wording on a balance is how a support email starts.
 */
export function describeBalance(balanceCents) {
	if (typeof balanceCents !== 'number' || !Number.isFinite(balanceCents)) {
		return { tone: 'neutral', text: 'No billing activity yet.' };
	}
	if (balanceCents === 0) {
		return { tone: 'success', text: "You're all paid up." };
	}
	if (balanceCents < 0) {
		return {
			tone: 'success',
			text: `You're paid ahead by ${formatCents(-balanceCents, { showCredit: false })}.`,
		};
	}
	return {
		tone: 'warning',
		text: `${formatCents(balanceCents)} is currently outstanding.`,
	};
}

/**
 * Sum a ledger into a balance. The authoritative balance is the cached
 * `billingProfiles.balanceCents` written server-side in the same transaction as
 * each entry; this recomputation exists so a surface can show the cache and the
 * ledger disagreeing rather than silently trusting the faster one.
 */
export function balanceFromLedger(entries) {
	return (entries || []).reduce((sum, e) => sum + (e.amountCents || 0), 0);
}

/**
 * Apply payments to invoices oldest-first and return each invoice's derived
 * payment state.
 *
 * Only invoices with status 'open' participate — a voided invoice has already
 * been reversed by a compensating ledger entry, so letting it absorb payment
 * would double-count the reversal and show a paid month as unpaid.
 *
 * Returns invoices in the order given, each with `paidCents`, `outstandingCents`
 * and `paymentStatus` ('paid' | 'partial' | 'unpaid').
 */
export function allocatePayments(invoices, ledgerEntries) {
	const open = (invoices || [])
		.filter((inv) => inv.status !== 'void')
		.slice()
		.sort((a, b) => String(a.periodMonth).localeCompare(String(b.periodMonth)));

	// Total money in, as a positive pool. Crediting entries are stored negative,
	// so flipping the sign here is what turns "owes less" into "pool to spend".
	let pool = (ledgerEntries || [])
		.filter((e) => CREDITING_TYPES.has(e.type))
		.reduce((sum, e) => sum + Math.abs(e.amountCents || 0), 0);

	const byId = new Map();
	open.forEach((inv) => {
		const due = inv.subtotalCents || 0;
		const paid = Math.min(pool, due);
		pool -= paid;
		byId.set(inv.id, {
			paidCents: paid,
			outstandingCents: due - paid,
			paymentStatus: paid >= due && due > 0 ? 'paid' : paid > 0 ? 'partial' : 'unpaid',
		});
	});

	return (invoices || []).map((inv) => ({
		...inv,
		...(byId.get(inv.id) || {
			paidCents: 0,
			outstandingCents: inv.status === 'void' ? 0 : inv.subtotalCents || 0,
			paymentStatus: inv.status === 'void' ? 'void' : 'unpaid',
		}),
	}));
}

/** "2026-09" -> "September 2026". Parsed by parts, never `new Date(string)`. */
export function formatPeriodMonth(periodMonth) {
	if (typeof periodMonth !== 'string' || !/^\d{4}-\d{2}$/.test(periodMonth)) return periodMonth || '—';
	const [year, month] = periodMonth.split('-');
	const names = [
		'January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December',
	];
	return `${names[Number(month) - 1]} ${year}`;
}

/**
 * "2026-09-01" -> "September 1, 2026".
 *
 * Split on the hyphens and build a LOCAL date rather than `new Date(dateStr)`,
 * which parses a bare date as UTC midnight and renders the previous day for
 * anyone west of Greenwich. That exact bug shipped once already, in the private
 * spaces import (STATE.md 2026-07-30), and a due date landing a day early on a
 * billing page is the version of it that generates phone calls.
 */
export function formatDateOnly(dateStr) {
	if (typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr || '—';
	const [y, m, d] = dateStr.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
}

/** Human label for a ledger entry, for the history list. */
export function describeLedgerEntry(entry) {
	if (!entry) return '';
	switch (entry.type) {
		case 'invoice_issued':
			return `Tuition — ${formatPeriodMonth((entry.invoiceId || '').split('__')[1])}`;
		case 'payment':
			return entry.paymentMethod === 'mms'
				? 'Payment received'
				: `Payment received (${(entry.paymentMethod || '').replace(/_/g, ' ')})`;
		case 'refund':
			return 'Refund issued';
		case 'credit':
			return 'Credit applied';
		case 'writeoff':
			return 'Balance adjusted';
		case 'invoice_voided':
			return 'Invoice cancelled';
		case 'adjustment':
			return 'Account adjustment';
		default:
			return 'Account activity';
	}
}
